import { useState, useEffect } from 'react';
import { Search, UserPlus, Loader2, Users } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { AvatarCircle } from '@/components/avatar-circle';
import { useSearchStudentsByName, type StudentSearchResult } from '@/lib/tutor/queries';
import { useInviteStudentById } from '@/lib/tutor/mutations';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

interface StudentNameSearchProps {
  tutorId: string;
  /** Matière(s) à associer au lien créé (migration 0227) — vide = toutes matières. */
  subjects?: SubjectSlug[];
  onInvited?: () => void;
}

export function StudentNameSearch({ tutorId, subjects = [], onInvited }: StudentNameSearchProps) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedPattern, setDebouncedPattern] = useState('');
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPattern(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: results, isLoading, isFetching } = useSearchStudentsByName(debouncedPattern);
  const invite = useInviteStudentById();

  async function handleInvite(student: StudentSearchResult) {
    await invite.mutateAsync({ tutorId, studentId: student.id, subjects });
    setInvitedIds((prev) => new Set(prev).add(student.id));
    onInvited?.();
  }

  const showResults = debouncedPattern.trim().length >= 2;
  const isSearching = isLoading || isFetching;

  return (
    <div className="space-y-3">
      {/* Search input */}
      <div className="relative">
        <Search className="text-ink-400 pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2" />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Rechercher par nom de profil…"
          autoFocus
          className="border-ink-200 bg-ink-50 text-ink-900 placeholder:text-ink-400 focus:border-primary-500 h-11 w-full rounded-xl border pl-10 pr-4 font-sans text-sm transition-colors focus:bg-white focus:outline-none"
        />
        {isSearching && (
          <Loader2 className="text-ink-400 absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin" />
        )}
      </div>

      {/* Results */}
      {showResults && (
        <div className="border-ink-200 overflow-hidden rounded-xl border bg-white">
          {!isSearching && results?.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
              <Users className="text-ink-300 h-8 w-8" />
              <p className="text-ink-500 font-sans text-sm">
                Aucun élève trouvé pour « {debouncedPattern} »
              </p>
              <p className="text-ink-400 font-sans text-xs">
                Vérifie l'orthographe ou essaie un prénom seul.
              </p>
            </div>
          ) : (
            <ul>
              {(results ?? []).map((student, i) => (
                <StudentResultRow
                  key={student.id}
                  student={student}
                  last={i === (results?.length ?? 0) - 1}
                  invited={invitedIds.has(student.id)}
                  isPending={invite.isPending && invite.variables?.studentId === student.id}
                  onInvite={() => handleInvite(student)}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      {!showResults && inputValue.trim().length > 0 && inputValue.trim().length < 2 && (
        <p className="text-ink-400 px-1 font-sans text-xs">
          Saisis au moins 2 caractères pour lancer la recherche.
        </p>
      )}

      {invite.error && (
        <p className="text-error-500 bg-error-50 rounded-xl px-3 py-2 font-sans text-sm">
          {invite.error.message}
        </p>
      )}
    </div>
  );
}

function StudentResultRow({
  student,
  last,
  invited,
  isPending,
  onInvite,
}: {
  student: StudentSearchResult;
  last: boolean;
  invited: boolean;
  isPending: boolean;
  onInvite: () => void;
}) {
  const levelLabel = student.level
    ? (LEVEL_LABEL[student.level as SchoolLevel] ?? student.level)
    : null;

  return (
    <li className={cn('flex items-center gap-3 px-4 py-3', !last && 'border-ink-100 border-b')}>
      <AvatarCircle name={student.display_name} src={student.avatar_url} size="md" />

      <div className="min-w-0 flex-1">
        <p className="font-display text-ink-900 truncate text-sm font-bold">
          {student.display_name}
        </p>
        {levelLabel && <p className="text-ink-500 mt-0.5 font-sans text-xs">{levelLabel}</p>}
      </div>

      <button
        onClick={onInvite}
        disabled={isPending || invited}
        className={cn(
          'font-display flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-3 text-xs font-bold transition-colors',
          invited
            ? 'bg-xp-50 text-xp-600 cursor-default'
            : 'bg-primary-50 text-primary-700 hover:bg-primary-100 active:scale-[0.98]',
        )}
      >
        {isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : invited ? (
          'Invité ✓'
        ) : (
          <>
            <UserPlus className="h-3.5 w-3.5" />
            Inviter
          </>
        )}
      </button>
    </li>
  );
}
